"use client";

import { trackUmamiEvent } from "@analytics";
import { VIDEO_HERO_EXAMPLE_EVENT } from "@marketing/home/components/HeroControls";
import type { HeroShowcaseExample } from "@marketing/home/components/VideoGeneratorHero";
import {
	HOME_HERO_FALLBACK_MODEL,
	HOME_HERO_MODELS,
} from "@marketing/home/config/home-hero-models";
import { getModelDisplayName } from "@marketing/home/config/model-display-names";
import { config } from "@repo/config";
import {
	ImageRefPicker,
	type ImageRefPickerHandle,
} from "@saas/ai-image-generator/components/ImageRefPicker";
import {
	PanelResizer,
	useLeftPanelWidth,
} from "@saas/ai-image-generator/components/PanelResizer";
import {
	PromptTextarea,
	type PromptTextareaHandle,
} from "@saas/ai-image-generator/components/PromptTextarea";
import {
	dropAllImageRefs,
	dropImageRefsAbove,
	firstMissingImageRef,
	formatImageRef,
	removeImageRef,
} from "@saas/ai-image-generator/lib/image-refs";
import { useSession } from "@saas/auth/hooks/use-session";
import { CreditsPaywallModal } from "@saas/payments/components/CreditsPaywallModal";
import { usePurchases } from "@saas/payments/hooks/purchases";
import { ImageDetailModal } from "@saas/shared/assets/ImageDetailModal";
import { SelectAssetDialog } from "@saas/shared/assets/SelectAssetDialog";
import { UploadPreviewDialog } from "@saas/shared/assets/UploadPreviewDialog";
import { UploadThumbnailActions } from "@saas/shared/assets/UploadThumbnailActions";
import { VideoDetailModal } from "@saas/shared/assets/VideoDetailModal";
import { AspectRatioSwatch } from "@saas/shared/components/AspectRatioSwatch";
import { useConfirmationAlert } from "@saas/shared/components/ConfirmationAlertProvider";
import { GenerationSettings } from "@saas/shared/components/GenerationSettings";
import { ModelBrandIcon } from "@saas/shared/components/ModelBrandIcon";
import {
	ModelPicker,
	ModelPickerItem,
} from "@saas/shared/components/ModelPicker";
import {
	type FeedView,
	HistoryFeedTabs,
} from "@saas/shared/history/HistoryFeedTabs";
import type { ImageActionLabels } from "@saas/shared/history/HistoryImageActions";
import { ImageHistoryCard } from "@saas/shared/history/ImageHistoryCard";
import { VideoHistoryCard } from "@saas/shared/history/VideoHistoryCard";
import { WorkbenchTemplates } from "@saas/shared/history/WorkbenchTemplates";
import { useImageHomeRouter } from "@saas/shared/history/image-home";
import type {
	HistoryFilter,
	ImageHistoryItem,
	VideoHistoryItem,
} from "@saas/shared/history/types";
import {
	GENERATION_HISTORY_QUERY_KEY,
	groupHistoryEntries,
} from "@saas/shared/history/types";
import { useVideoFavorite } from "@saas/shared/history/use-video-favorite";
import { flashMissingField } from "@saas/shared/lib/flash-missing-field";
import { buildDisplayImageUrl } from "@saas/shared/lib/image-url-builder";
import { useRouter } from "@shared/hooks/router";
import { apiClient } from "@shared/lib/api-client";
import { downloadWithWatermark } from "@shared/lib/watermark-canvas";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@ui/components/button";
import { Label } from "@ui/components/label";
import { Slider } from "@ui/components/slider";
import { cn } from "@ui/lib";
import {
	AtSignIcon,
	ChevronDownIcon,
	ChevronsRightIcon,
	FolderIcon,
	GemIcon,
	Loader2Icon,
	LockIcon,
	UploadIcon,
} from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import dynamic from "next/dynamic";
import { useCallback, useEffect, useRef, useState } from "react";
import { useInView } from "react-intersection-observer";
import { toast } from "sonner";
import { getI2VModelBrand } from "../config/model-brands";
import type { I2VSampleExample } from "../config/sample-examples";
import {
	type I2VInputImage,
	type UseImageToVideoOptions,
	useImageToVideo,
} from "../hooks/use-image-to-video";
import type {
	I2VAspectRatio,
	I2VMode,
	I2VModelId,
	I2VResolution,
} from "../types";
import {
	I2V_MODES,
	collapseI2VModelFamilies,
	getI2VAspectRatios,
	getI2VCreditCost,
	getI2VDurationOptions,
	getI2VModel,
	getI2VModelFamily,
	getI2VModelPriceHint,
	getI2VPromptMaxLength,
	getMaxI2VImages,
	supportsI2VReferenceAudio,
	supportsI2VReferenceVideo,
} from "../types";
import { FrameSlot } from "./FrameSlot";
import {
	type MusicVideoUploadLabels,
	MusicVideoUploads,
} from "./MusicVideoUploads";
import {
	ACCEPTED_REFERENCE_MEDIA_TYPES,
	ReferenceMediaThumb,
	getReferenceMediaKind,
} from "./ReferenceMediaThumb";
import {
	type VideoEditUploadLabels,
	VideoEditUploads,
} from "./VideoEditUploads";

/**
 * 下拉换成首页那份名单后，basic-between / master-3.0 不在里面了，但历史回填、登录前
 * 现场、切 tab 还会把它们带进表单。两者在名单里都有同上游同价的那档，换过去而不是
 * 掉回 Basic：master-3.0 的 480p/720p 实际跑的就是 Seedance 2.0 Fast。
 */
function toListedI2VModel(
	model: I2VModelId,
	mode: I2VMode,
	resolution: I2VResolution,
): I2VModelId {
	if (model === "basic-between") {
		return "seedance-2-mini";
	}
	if (model === "master-3.0") {
		return resolution === "480p" || resolution === "720p"
			? "seedance-2-fast"
			: "seedance-2";
	}
	return HOME_HERO_FALLBACK_MODEL[mode];
}

// 空状态的示例区。素材由我们自己的模型跑出来，存在 R2，见 config/sample-examples.ts。
const I2VSampleShowcase = dynamic(
	() => import("./I2VSampleShowcase").then((mod) => mod.I2VSampleShowcase),
	{ ssr: false, loading: () => <div className="min-h-0 flex-1" /> },
);

const EditUploadedImageDialog = dynamic(
	() =>
		import("@saas/shared/assets/EditUploadedImageDialog").then(
			(mod) => mod.EditUploadedImageDialog,
		),
	{ ssr: false },
);

const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_IMAGE_SIZE_MB = 30;
/** sonner 2.0.1 的 Toast 退场 200ms 后卸载（TIME_BEFORE_UNMOUNT），再加一帧给它关列表的 requestAnimationFrame */
const REF_TOAST_EXIT_MS = 300;

/**
 * 上传区高度。刻意不让它吃掉整块剩余空间：撑高的空 dropzone 只是更大一片空白，
 * 首尾帧那两个槽还会被拉成瘦高条、不像图片框。剩余空间交给 prompt——愿意写长
 * 提示词的人真的用得上。
 * 矮屏（可视高度 ≤780，1366×768 这类，电脑访客约两成）降到 160：上面多了模型卡、
 * 配置条也跟着 prompt 走，240 会把配置条挤出屏幕；160 和空框自身高度差不多，传图前后不跳（10/24）
 */
const SLOT_MIN_HEIGHT =
	"min-h-[200px] sm:min-h-[240px] studio-split:[@media(max-height:780px)]:min-h-[160px]";
/** 底部设置胶囊和弹框里的选项，和 AiImageGeneratorClient 的设置弹框同款 */
const SETTINGS_BAR_CLASS =
	"group flex h-12 w-full min-w-0 shrink-0 items-center rounded-xl border border-black/5 bg-zinc-100 px-3 text-sm font-semibold text-zinc-700 transition-colors hover:bg-zinc-200/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 data-[state=open]:bg-zinc-200/70";
const SETTINGS_LABEL_CLASS = "text-xs font-medium text-zinc-700";
const SETTINGS_OPTION_CLASS =
	"flex h-9 items-center justify-center gap-1 rounded-xl px-2 text-[13px] font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40";
const SETTINGS_OPTION_SELECTED = "bg-zinc-950 text-white shadow-sm";
const SETTINGS_OPTION_IDLE =
	"bg-zinc-100 text-zinc-600 hover:bg-zinc-200 hover:text-zinc-900";
/** 首尾帧两个并排，各占一半宽，比单槽矮一档：两个框叠起来太高会把 prompt 挤下去 */
const SLOT_MIN_HEIGHT_SPLIT = "min-h-[150px]";
const MOBILE_HEADER_HEIGHT = 72;
const MOBILE_SCROLL_GAP = 12;

function getMobileScrollTop(element: HTMLElement): number {
	const bannerHeight = Number.parseFloat(
		window
			.getComputedStyle(document.documentElement)
			.getPropertyValue("--language-banner-height"),
	);
	return Math.max(
		0,
		element.getBoundingClientRect().top +
			window.scrollY -
			MOBILE_HEADER_HEIGHT -
			(Number.isNaN(bannerHeight) ? 0 : bannerHeight) -
			MOBILE_SCROLL_GAP,
	);
}

/** 落地页复用工作台时传（/start-end-frame-video）：标题区换本页文案，首屏停在本页的 tab 和模型 */
interface ImageToVideoClientProps {
	titleOverride?: string;
	subtitleOverride?: string;
	initial?: UseImageToVideoOptions["initial"];
	pendingStateKey?: string;
	/**
	 * 改视频页（/genjutsu-ai）：锁在 Reference、不显示三个 tab，模型只留 models，上传拆成视频格 + 照片格，
	 * 两格都必填（见 UseImageToVideoOptions.requireReferenceVideo）。照 AIReel 精简（11/03 老公定）
	 */
	videoEdit?: { models: I2VModelId[] } & (
		| { uploads?: "video"; labels: VideoEditUploadLabels }
		/**
		 * 对口型 MV 页（/ai-music-video-generator）：上传拆成歌 + 照片，歌可截 4–60 秒；
		 * 时长跟歌走、比例跟照片走、只有 720p，设置条整个不出
		 */
		| { uploads: "audio"; labels: MusicVideoUploadLabels }
	);
	/** Prompt 上方的一排胶囊，点了用整段 prompt 覆盖输入框（/genjutsu-ai 的三种编辑模式） */
	promptPresets?: { id: string; label: string; prompt: string }[];
	/** 右栏 Examples 换成本页的样片；不传用全站通用那组 */
	sampleExamples?: I2VSampleExample[];
}

export function ImageToVideoClient({
	titleOverride,
	subtitleOverride,
	initial,
	pendingStateKey,
	videoEdit,
	promptPresets,
	sampleExamples,
}: ImageToVideoClientProps = {}) {
	const t = useTranslations("imageToVideo.client");
	const tAssets = useTranslations("assets");
	const tVideo = useTranslations("aiVideoGenerator.client");
	const tImage = useTranslations("aiImageGenerator.client");
	const { width: leftPanelWidth, commit: commitLeftPanelWidth } =
		useLeftPanelWidth();

	const { user } = useSession();
	const {
		canRemoveWatermark,
		loading: purchasesLoading,
		isError: purchasesError,
	} = usePurchases();
	const [historyFilter, setHistoryFilter] = useState<HistoryFilter>("all");
	// null = 用户还没点过右栏的 tab，按有没有作品自动选（见 activeView）
	const [feedView, setFeedView] = useState<FeedView | null>(null);

	const {
		mode,
		setMode,
		model,
		setModel,
		modelConfig,
		inputImages,
		filledImages,
		maxImages,
		hasRequiredImages,
		setImageAt,
		addImageFiles,
		addExistingImages,
		removeImage,
		restoreImageAt,
		replaceImage,
		clearImages,
		referenceMedia,
		referenceVideos,
		referenceAudios,
		addReferenceMedia,
		setDrivingAudio,
		removeReferenceMedia,
		videoLimits,
		audioLimits,
		referenceVideoSeconds,
		isProcessingMedia,
		prompt,
		setPrompt,
		resolution,
		setResolution,
		lockedResolutions,
		aspectRatio,
		canPickAspectRatio,
		setAspectRatio,
		duration,
		setDuration,
		outputs,
		setOutputs,
		creditCost,
		isPending,
		isUploading,
		applyHistoryItem,
		setExample,
		handleGenerate,
		latestStartedBatchId,
		history,
		isHistoryLoading,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
		paywallReason,
		setPaywallReason,
	} = useImageToVideo(historyFilter, {
		initial,
		pendingStateKey,
		requireReferenceVideo: !!videoEdit && videoEdit.uploads !== "audio",
	});
	const musicVideo = videoEdit?.uploads === "audio" ? videoEdit : null;

	const aspectRatioOptions = [...getI2VAspectRatios(model, mode)].sort(
		(a, b) => Number(b === "auto") - Number(a === "auto"),
	);

	const [isDragOver, setIsDragOver] = useState(false);
	// 素材库对话框：数字表示往哪个固定槽位放，"append" 是 reference 的追加
	const [assetTarget, setAssetTarget] = useState<number | "append" | null>(
		null,
	);
	const [previewIndex, setPreviewIndex] = useState<number | null>(null);
	const [editIndex, setEditIndex] = useState<number | null>(null);
	// Prompt 里的 @ Reference，照 /ai-edit-image（10/22）：只在 reference tab，弹 My Assets，选图后插「Image N」
	const [isRefPickerOpen, setIsRefPickerOpen] = useState(false);
	// 光标停在哪个「Image N」上，参考区那张描边
	const [activeImageRef, setActiveImageRef] = useState<number | null>(null);
	const promptHandleRef = useRef<PromptTextareaHandle>(null);
	const refPickerHandleRef = useRef<ImageRefPickerHandle>(null);
	const refButtonRef = useRef<HTMLButtonElement>(null);
	const promptBoxRef = useRef<HTMLDivElement>(null);
	// 从 @ 弹层点 Browse all 打开的素材弹框，选完要把编号插进 prompt
	const browseInsertsRefsRef = useRef(false);
	// 删图 Undo 只在删完之后什么都没动时有效：记下当时的 tab、上限、剩下的图和删完的 prompt，一变就把 Toast 关掉。
	// 不看 prompt 的话，删完接着打字再点 Undo，会用删之前的整段把新打的字盖掉（10/22 Codex）
	const removeUndoRef = useRef<{
		toastId: string | number;
		mode: I2VMode;
		maxImages: number;
		images: I2VInputImage[];
		prompt: string;
	} | null>(null);
	// 删图 Undo 的 Toast 自己到点 / 被划掉开始退场后，到这个时间点为止（ms）。sonner 2.0.1 退场 200ms 后按 id 卸载：
	// 这期间同 id 原地换内容会跟着被卸掉，第二个 Undo 一闪都没有（10/23 复现，Codex 审出来的）；换新 id 马上建
	// 又可能撞上「关 Toast 拿旧列表整个覆盖」。所以退场中不碰它，等卸载完再用新 id 出，排着的那条记在 pendingRefToastRef
	const refToastExitUntilRef = useRef(0);
	const pendingRefToastRef = useRef<number | undefined>(undefined);
	const [selectedVideo, setSelectedVideo] = useState<VideoHistoryItem | null>(
		null,
	);
	// 历史里的图片沿用图片生成页那一套：详情弹窗按「记录 + 下标」定位到具体某一张
	const [selectedImage, setSelectedImage] = useState<{
		item: ImageHistoryItem;
		imageIndex: number;
	} | null>(null);
	// 收藏是单张图级别的，key 是 `${生成记录 id}:${图片下标}`
	const [favoriteOverrides, setFavoriteOverrides] = useState<
		Record<string, boolean>
	>({});
	// Regenerate 要等一帧：applyHistoryItem 设完表单，handleGenerate 下一轮才看得到
	const [regenerateRequested, setRegenerateRequested] = useState(false);
	// 示例区的 Create 也要等一帧，原因见下面 handleUseSample 的注释
	const [pendingSample, setPendingSample] = useState<I2VSampleExample | null>(
		null,
	);

	const referenceInputRef = useRef<HTMLInputElement>(null);
	const historyScrollRef = useRef<HTMLDivElement>(null);
	const latestBatchRef = useRef<HTMLDivElement>(null);
	const scrolledBatchIdRef = useRef<string | null>(null);
	const queryClient = useQueryClient();
	const { confirm } = useConfirmationAlert();
	const { isFavorite: isVideoFavorite, toggleFavorite: toggleVideoFavorite } =
		useVideoFavorite();

	const actionLabels: ImageActionLabels = {
		generateVideo: tAssets("detail.generateVideo"),
		download: tAssets("buttons.download"),
		more: tAssets("buttons.more"),
		edit: tAssets("buttons.edit"),
		delete: tAssets("delete"),
		favorite: tAssets("buttons.favorite"),
	};

	const { ref: loadMoreRef, inView } = useInView({ rootMargin: "200px" });
	useEffect(() => {
		if (inView && hasNextPage && !isFetchingNextPage) {
			fetchNextPage();
		}
	}, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

	// 访客只有示例和模板。登录用户点过 tab 就听用户的；没点过时有作品看作品、没作品看示例，
	// 历史还在加载就先按作品转圈，免得示例视频先开播再被换掉
	const activeView: FeedView = !user
		? feedView === "templates"
			? "templates"
			: "examples"
		: (feedView ??
			(history.length > 0 || isHistoryLoading ? "mine" : "examples"));

	// 开始生成就切到我的作品，停在示例 tab 上看不到进度
	useEffect(() => {
		if (latestStartedBatchId) {
			setFeedView("mine");
		}
	}, [latestStartedBatchId]);

	// 等新任务卡片出现后定位一次；轮询刷新和同批追加输出不抢走滚动位置。
	useEffect(() => {
		const card = latestBatchRef.current;
		const container = historyScrollRef.current;
		if (
			!latestStartedBatchId ||
			scrolledBatchIdRef.current === latestStartedBatchId ||
			!card ||
			!container
		) {
			return;
		}

		scrolledBatchIdRef.current = latestStartedBatchId;
		const behavior = window.matchMedia("(prefers-reduced-motion: reduce)")
			.matches
			? "auto"
			: "smooth";
		if (window.innerWidth >= 768) {
			container.scrollTo({
				top:
					container.scrollTop +
					card.getBoundingClientRect().top -
					container.getBoundingClientRect().top -
					Number.parseFloat(
						window.getComputedStyle(container).paddingTop,
					),
				behavior,
			});
		} else {
			container.scrollTo({ top: 0, behavior: "instant" });
			window.scrollTo({ top: getMobileScrollTop(card), behavior });
		}
	}, [history, latestStartedBatchId]);

	useEffect(() => {
		if (!regenerateRequested) {
			return;
		}
		setRegenerateRequested(false);
		void handleGenerate();
	}, [regenerateRequested, handleGenerate]);

	/** 类型和大小校验，不合格的丢掉并提示 */
	const acceptFiles = useCallback(
		(files: File[]): File[] => {
			const accepted: File[] = [];
			for (const file of files) {
				if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
					toast.error(t("invalidImageType"));
					continue;
				}
				if (file.size > MAX_IMAGE_SIZE_MB * 1024 * 1024) {
					toast.error(
						t("imageTooLarge", { size: MAX_IMAGE_SIZE_MB }),
					);
					continue;
				}
				accepted.push(file);
			}
			return accepted;
		},
		[t],
	);

	const handleSlotFiles = useCallback(
		(index: number, files: File[]) => {
			const accepted = acceptFiles(files);
			if (!accepted.length) {
				return;
			}
			setImageAt(index, {
				previewUrl: URL.createObjectURL(accepted[0]),
				file: accepted[0],
			});
		},
		[acceptFiles, setImageAt],
	);

	const handleReferenceFiles = useCallback(
		(files: File[]) => {
			const media = files.flatMap((file) => {
				const kind = getReferenceMediaKind(file);
				return kind ? [{ file, kind }] : [];
			});
			if (media.length) {
				const listed = (videoEdit?.models ?? HOME_HERO_MODELS).filter(
					(id) => getI2VModel(id).modes.includes("reference"),
				);
				const { switchedTo, skipped } = addReferenceMedia(
					media,
					listed,
				);
				if (switchedTo) {
					toast.info(
						t("switchedForMedia", {
							from: getModelDisplayName(model),
							to: getModelDisplayName(switchedTo),
						}),
					);
				}
				if (skipped) {
					const config = getI2VModel(switchedTo ?? model);
					toast.error(
						t("maxMediaReached", {
							videos: config.referenceVideo?.maxCount ?? 0,
							audios: config.referenceAudio?.maxCount ?? 0,
						}),
					);
				}
			}
			const accepted = acceptFiles(
				files.filter((file) => !getReferenceMediaKind(file)),
			);
			if (!accepted.length) {
				return;
			}
			const skipped = addImageFiles(accepted);
			if (skipped > 0) {
				toast.error(t("maxImagesReached", { max: maxImages }));
			}
		},
		[
			acceptFiles,
			addImageFiles,
			addReferenceMedia,
			maxImages,
			model,
			t,
			videoEdit,
		],
	);

	const handleAssetConfirm = useCallback(
		(urls: string[]) => {
			if (assetTarget === null || !urls.length) {
				return;
			}
			if (assetTarget === "append") {
				const previews = filledImages.map((image) => image.previewUrl);
				const newUrls = urls.filter((url) => !previews.includes(url));
				// 从 @ 弹层的 Browse all 进来的：已在参考区的复用编号，新图排在最后，按选图顺序插编号
				if (browseInsertsRefsRef.current) {
					browseInsertsRefsRef.current = false;
					const nextPreviews = [...previews, ...newUrls];
					promptHandleRef.current?.insertImageRefs(
						urls.map((url) => nextPreviews.indexOf(url) + 1),
					);
					void trackUmamiEvent("i2v-reference-inserted", {
						from: "browse",
						count: urls.length,
					});
				}
				addExistingImages(newUrls);
			} else {
				setImageAt(assetTarget, { previewUrl: urls[0] });
			}
			setAssetTarget(null);
		},
		[assetTarget, addExistingImages, setImageAt, filledImages],
	);

	// 素材库读的是登录用户的素材：没登录先弹登录框，别打开一个加载失败的空弹框（跟生图页一样）
	const openAssetPicker = (target: number | "append") => {
		if (!user) {
			window.dispatchEvent(
				new CustomEvent("show-login", {
					detail: {
						redirectTo:
							window.location.pathname + window.location.search,
					},
				}),
			);
			return;
		}
		setAssetTarget(target);
	};

	// Toast 自己开始退场（到点、被划掉、点关闭）：记下退场到几时；它带的 Undo 也作废。
	// 点 Undo 和下面 effect 主动关的那两条照旧：引用当场清空，下一条直接用新 id
	const markRefToastLeaving = (toastId: string | number) => {
		refToastExitUntilRef.current = Date.now() + REF_TOAST_EXIT_MS;
		if (removeUndoRef.current?.toastId === toastId) {
			removeUndoRef.current = null;
		}
	};

	// 删图 / 清空的提示。同一时间只留一条：还在显示的那条原地换内容，不要先 dismiss 再新建
	//（sonner 2.0.1 关 Toast 时拿旧列表整个覆盖，同一刻新建的那个会被吞掉）；没有或正在退场就用新 id，
	// 退场中的话等它卸载完再出
	const showRefToast = (
		message: string,
		action?: { label: string; onClick: () => void },
	) => {
		const toastId = removeUndoRef.current?.toastId ?? crypto.randomUUID();
		const show = () => {
			pendingRefToastRef.current = undefined;
			toast(message, {
				id: toastId,
				action,
				onAutoClose: () => markRefToastLeaving(toastId),
				onDismiss: () => markRefToastLeaving(toastId),
			});
		};
		window.clearTimeout(pendingRefToastRef.current);
		const wait = refToastExitUntilRef.current - Date.now();
		if (wait > 0) {
			pendingRefToastRef.current = window.setTimeout(show, wait);
		} else {
			show();
		}
		return toastId;
	};

	// reference tab 删图：指向这张的「Image N」删掉、后面的编号往前补，否则会悄悄改指下一张；给 Undo
	const removeReferenceImage = (index: number) => {
		const image = filledImages[index];
		const nextPrompt = removeImageRef(prompt, index + 1);
		if (image && nextPrompt !== prompt) {
			const previousPrompt = prompt;
			// 同一时间只留一个有效的 Undo：连删两张时第一个已经对不上了，换成这一次的
			const toastId = showRefToast(
				tImage("imageRefs.removedFromPrompt", { n: index + 1 }),
				{
					label: tImage("imageRefs.undo"),
					onClick: () => {
						removeUndoRef.current = null;
						restoreImageAt(index, image);
						setPrompt(previousPrompt);
					},
				},
			);
			removeUndoRef.current = {
				toastId,
				mode,
				maxImages,
				images: filledImages.filter((_, i) => i !== index),
				prompt: nextPrompt,
			};
			setPrompt(nextPrompt);
		}
		removeImage(index);
	};

	// 删完之后切了 tab、加删了图、裁剪换了图、上限变了、改了 prompt：Undo 放回去会和现状对不上，直接撤掉
	useEffect(() => {
		const pending = removeUndoRef.current;
		if (
			pending &&
			(pending.mode !== mode ||
				pending.maxImages !== maxImages ||
				pending.prompt !== prompt ||
				pending.images.length !== filledImages.length ||
				pending.images.some((image, i) => image !== filledImages[i]))
		) {
			// 还排着没出的那条（等上一条退场时删的）直接取消，出了的关掉
			window.clearTimeout(pendingRefToastRef.current);
			pendingRefToastRef.current = undefined;
			toast.dismiss(pending.toastId);
			removeUndoRef.current = null;
		}
	}, [mode, maxImages, prompt, filledImages]);

	// 换模型张数上限变小时 hook 会砍掉后面的图，指向它们的编号一起删
	const changeModel = (next: I2VModelId) => {
		if (mode === "reference") {
			const limit = getMaxI2VImages(next, mode);
			if (filledImages.length > limit) {
				setPrompt(dropImageRefsAbove(prompt, limit));
			}
		}
		setModel(next);
	};

	// @ Reference：按钮或在 prompt 里词首敲 @ 打开
	const openRefPicker = (via: "button" | "typed") => {
		if (via === "button") {
			promptHandleRef.current?.cancelImageRef();
		}
		setIsRefPickerOpen(true);
		void trackUmamiEvent("i2v-reference-opened", { via });
	};

	const closeRefPicker = useCallback(() => {
		promptHandleRef.current?.cancelImageRef();
		setIsRefPickerOpen(false);
	}, []);

	const insertExistingImageRef = (n: number) => {
		promptHandleRef.current?.insertImageRefs([n]);
		setIsRefPickerOpen(false);
		void trackUmamiEvent("i2v-reference-inserted", { from: "in-use" });
	};

	// 素材库里的图：先加进参考区当最后一张，再插它的编号
	const insertAssetImageRef = (url: string, from: "recent" | "favorite") => {
		if (filledImages.length >= maxImages) {
			return;
		}
		promptHandleRef.current?.insertImageRefs([filledImages.length + 1]);
		addExistingImages([url]);
		setIsRefPickerOpen(false);
		void trackUmamiEvent("i2v-reference-inserted", { from });
	};

	const browseAllForRef = () => {
		setIsRefPickerOpen(false);
		browseInsertsRefsRef.current = true;
		openAssetPicker("append");
	};

	// 没登录又没传图时什么都不弹（跟 /ai-edit-image 一样不放登录框）
	// 对口型页只收一张照片，OmniHuman 也不认 prompt 里的「Image N」，不给 @ Reference
	const canReferenceImages =
		mode === "reference" &&
		!musicVideo &&
		(!!user || filledImages.length > 0);

	// prompt 里写了不存在的编号（Image 4 但只有 2 张）：只提示，不拦 Create
	const missingImageRef =
		mode === "reference" && filledImages.length > 0
			? firstMissingImageRef(prompt, filledImages.length)
			: null;

	// 粘贴图片跟拖进来一样，正在 prompt 框里打字也算；剪贴板是文字就照常粘贴。
	// 素材库弹框开着时它自己接粘贴，这里让开；裁剪弹框开着时也不动槽位
	useEffect(() => {
		if (mode === "text" || assetTarget !== null || editIndex !== null) {
			return;
		}

		const handlePaste = (event: ClipboardEvent) => {
			const files = Array.from(event.clipboardData?.files ?? []);
			const isImage = (file: File) => file.type.startsWith("image/");
			if (mode === "reference") {
				// 参考区本来就收视频和音频，粘贴也一样
				if (
					!files.some(
						(file) => isImage(file) || getReferenceMediaKind(file),
					)
				) {
					return;
				}
				event.preventDefault();
				handleReferenceFiles(files);
				return;
			}

			const images = files.filter(isImage);
			if (images.length === 0) {
				return;
			}
			event.preventDefault();
			// 首尾帧先填空着的那一格，都满了跟生图页一样提示到上限
			const slots = mode === "between" ? 2 : 1;
			const index = [0, 1]
				.slice(0, slots)
				.find((slot) => !inputImages[slot]);
			if (index === undefined) {
				toast.error(t("maxImagesReached", { max: slots }));
				return;
			}
			handleSlotFiles(index, images);
		};
		window.addEventListener("paste", handlePaste);

		return () => window.removeEventListener("paste", handlePaste);
	}, [
		mode,
		assetTarget,
		editIndex,
		inputImages,
		handleReferenceFiles,
		handleSlotFiles,
		t,
	]);

	const handleEditConfirm = useCallback(
		async (blob: Blob) => {
			if (editIndex !== null) {
				replaceImage(
					editIndex,
					new File([blob], "cropped.webp", {
						type: blob.type || "image/webp",
					}),
				);
			}
			setEditIndex(null);
		},
		[editIndex, replaceImage],
	);

	const handleDeleteVideo = useCallback(
		(item: VideoHistoryItem) => {
			confirm({
				title: tAssets("deleteDialog.title"),
				message: tAssets("deleteDialog.descriptionVideo"),
				confirmLabel: tAssets("deleteDialog.confirm"),
				destructive: true,
				onConfirm: async () => {
					try {
						// 删除按行 id 走，跟模型无关，直接复用 ai-video 的端点
						const response = await apiClient["ai-video"][
							":taskId"
						].$delete({ param: { taskId: item.id } });
						if (!response.ok) {
							throw new Error("Failed to delete");
						}
						setSelectedVideo(null);
						queryClient.invalidateQueries({
							queryKey: GENERATION_HISTORY_QUERY_KEY,
						});
					} catch (error) {
						console.error(error);
						toast.error("Failed to delete. Please try again.");
					}
				},
			});
		},
		[confirm, queryClient, tAssets],
	);

	/**
	 * 示例区的 Create：把这条示例的整套参数原样搬进表单，用户直接点生成就能复现。
	 * 只填表单，不自动提交——真扣积分的那一下留给用户自己点。
	 *
	 * 必须分两拍：setMode 重建图槽（between 两个、reference 空列表），但 setModel
	 * 的图片数上限是按 **调用时** 的 mode 算的。同一批里连着调，setModel 会拿旧
	 * mode（start，上限 1）把刚建好的两个槽截成一个，第二张图就丢了。
	 * 所以这里只切 mode，其余等 mode 真正落定后在下面的 effect 里填。
	 */
	/**
	 * 移动端表单在示例区和历史区的**上面**，回填是在屏幕外发生的——
	 * 不滚上去，用户点完 Create / Reprompt 只会看到原地没动，以为按钮坏了。
	 * 桌面端是左右两栏、整页不滚，这里什么也不会发生。
	 */
	/** 移动端回填后要滚到表单；Reprompt 直接定位到 prompt */
	const formRef = useRef<HTMLElement>(null);
	const [modelMenuOpen, setModelMenuOpen] = useState(false);
	const promptSectionRef = useRef<HTMLDivElement>(null);

	const scrollToForm = useCallback(() => {
		const el = formRef.current;
		if (!el) {
			return;
		}
		// 两个坑都是实测出来的：
		// 1. 不能用 scrollIntoView——表单往上两层祖先是 overflow:hidden，浏览器会
		//    挑它们当滚动容器去滚，滚不动，窗口一动不动。
		// 2. 不能用 behavior: "smooth"——本页的平滑滚动整体失效（手动调也不动），
		//    而瞬时滚动正常。何况从示例区滚回表单有六千多像素，瞬时反而干脆。
		window.scrollTo(0, getMobileScrollTop(el));
	}, []);

	const scrollToPrompt = useCallback(() => {
		// applyHistoryItem 可能会切换输入模式，等新布局落定后再量 prompt 的位置。
		window.requestAnimationFrame(() => {
			if (window.innerWidth >= 768 || !promptSectionRef.current) {
				return;
			}
			window.scrollTo(0, getMobileScrollTop(promptSectionRef.current));
		});
	}, []);

	const handleReprompt = useCallback(
		(item: VideoHistoryItem) => {
			applyHistoryItem(item);
			scrollToPrompt();
		},
		[applyHistoryItem, scrollToPrompt],
	);

	const handleRegenerate = useCallback(
		(item: VideoHistoryItem) => {
			applyHistoryItem(item);
			setRegenerateRequested(true);
		},
		[applyHistoryItem],
	);

	const router = useRouter();
	const routeImageHome = useImageHomeRouter();
	const locale = useLocale();

	const imageGeneratorHref = (action: string) =>
		locale === config.i18n.defaultLocale
			? `/ai-edit-image?action=${action}`
			: `/${locale}/ai-edit-image?action=${action}`;

	const isImageFavorite = (item: ImageHistoryItem, imageIndex: number) =>
		favoriteOverrides[`${item.id}:${imageIndex}`] ??
		!!item.favoriteImageIndexes?.includes(imageIndex);

	const handleImageFavoriteToggle = async (
		id: string,
		imageIndex: number,
		currentFavorite: boolean,
	) => {
		const key = `${id}:${imageIndex}`;
		const newFavoriteState = !currentFavorite;
		setFavoriteOverrides((previous) => ({
			...previous,
			[key]: newFavoriteState,
		}));

		try {
			const response = await (
				apiClient["image-generation"][":id"].favorite.$patch as any
			)({
				param: { id },
				json: { favorite: newFavoriteState, imageIndex },
			});
			if (!response.ok) {
				throw new Error("Failed to update favorite");
			}
			queryClient.invalidateQueries({
				queryKey: GENERATION_HISTORY_QUERY_KEY,
			});
		} catch (error) {
			setFavoriteOverrides((previous) => ({
				...previous,
				[key]: currentFavorite,
			}));
			console.error(error);
			toast.error(tAssets("errors.favoriteFailed"));
		}
	};

	const handleImageDelete = (id: string, imageIndex: number) => {
		confirm({
			title: tAssets("deleteDialog.title"),
			message: tAssets("deleteDialog.description"),
			confirmLabel: tAssets("deleteDialog.confirm"),
			destructive: true,
			onConfirm: async () => {
				try {
					const response = await fetch(
						`/api/image-generation/${id}?imageIndex=${imageIndex}`,
						{ method: "DELETE", credentials: "include" },
					);
					if (!response.ok) {
						throw new Error("Failed to delete");
					}
					// 服务端删图后会把后面的收藏下标整体前移，按旧下标存的
					// 乐观状态就错位了，直接丢掉这条记录的
					setFavoriteOverrides((previous) =>
						Object.fromEntries(
							Object.entries(previous).filter(
								([key]) => !key.startsWith(`${id}:`),
							),
						),
					);
					setSelectedImage(null);
					queryClient.invalidateQueries({
						queryKey: GENERATION_HISTORY_QUERY_KEY,
					});
					toast.success(tAssets("notifications.deleteSuccess"));
				} catch (error) {
					console.error(error);
					toast.error(tAssets("errors.deleteFailed"));
				}
			},
		});
	};

	// 图片的编辑 / reprompt / regenerate 要回填的是图片表单，这一页只有视频表单，
	// 所以跳到图片生成页去做 —— sessionStorage 的 key 跟 My Assets 一致
	const handleImageEdit = (imageUrl: string) => {
		sessionStorage.setItem("editImageData", JSON.stringify({ imageUrl }));
		router.push(imageGeneratorHref("edit"));
	};

	const handleImageAction = (
		item: ImageHistoryItem,
		action: "reprompt" | "regenerate",
	) => {
		// 姿势页出的图回姿势页（image-home.ts），其余照旧去生图页
		if (routeImageHome(item, action)) {
			return;
		}
		if (action === "reprompt") {
			sessionStorage.setItem(
				"repromptData",
				JSON.stringify({
					prompt: item.originalPrompt,
					inputImageUrls: item.inputImageUrls || [],
					inputType: item.inputImageUrls?.length ? "image" : "text",
				}),
			);
		} else {
			sessionStorage.setItem(
				"regenerateData",
				JSON.stringify({ generation: item }),
			);
		}
		router.push(imageGeneratorHref(action));
	};

	/**
	 * 历史里的图片直接当输入图填进表单，省掉一次下载再上传。
	 * reference 是一条不带空洞的列表所以往后追加，start / between 是固定槽位，
	 * 要放进第一个空槽——append 会把槽位数量撑坏。
	 */
	const handleGenerateVideoFromImage = useCallback(
		(imageUrl: string) => {
			if (mode === "reference") {
				if (inputImages.length >= maxImages) {
					toast.error(t("maxImagesReached", { max: maxImages }));
					return;
				}
				addExistingImages([imageUrl]);
			} else {
				const emptySlot = inputImages.findIndex((image) => !image);
				if (emptySlot === -1) {
					toast.error(t("maxImagesReached", { max: maxImages }));
					return;
				}
				setImageAt(emptySlot, { previewUrl: imageUrl });
			}
			setSelectedImage(null);
			// 移动端表单在历史上面，不滚上去用户看不到图被填进了哪儿
			scrollToForm();
		},
		[
			mode,
			inputImages,
			maxImages,
			addExistingImages,
			setImageAt,
			scrollToForm,
			t,
		],
	);

	// 新片子只会出现在 All 和 Video 里，别把用户丢在 Image tab 上干等
	useEffect(() => {
		if (isPending) {
			setHistoryFilter("all");
		}
	}, [isPending]);

	/**
	 * 图片生成页 / My Assets 的 Generate Video 跳过来：只带那张图当首帧，
	 * prompt 和参数留给用户自己填——图片的描述写的是静止画面，对视频没用。
	 *
	 * 跟示例区一样要分两拍：setMode 会重建图槽，同一批里 setImageAt 打在旧数组上。
	 * 从 reference 模式（空列表）过来时尤其明显，那一下 map 不到任何下标，图就丢了。
	 */
	const [pendingStartImage, setPendingStartImage] = useState<string | null>(
		null,
	);

	useEffect(() => {
		if (
			new URLSearchParams(window.location.search).get("action") !==
			"image-to-video"
		) {
			return;
		}
		const raw = sessionStorage.getItem("imageToVideoData");
		if (!raw) {
			return;
		}
		sessionStorage.removeItem("imageToVideoData");
		try {
			const { imageUrl } = JSON.parse(raw) as { imageUrl: string };
			setMode("start");
			setPendingStartImage(imageUrl);
		} catch {
			// 数据坏了就当没跳转过，用户还能手动传
		}
	}, [setMode]);

	// 图片生成页和 My Assets 的历史里也会出现 image-to-video 的记录，那边认出来后
	// 把整条记录留在 sessionStorage 再跳过来，由这里回填表单。
	useEffect(() => {
		const action = new URLSearchParams(window.location.search).get(
			"action",
		);
		if (action !== "reprompt" && action !== "regenerate") {
			return;
		}
		const raw = sessionStorage.getItem("videoActionData");
		if (!raw) {
			return;
		}
		sessionStorage.removeItem("videoActionData");
		try {
			const item = JSON.parse(raw) as VideoHistoryItem;
			if (action === "regenerate") {
				handleRegenerate(item);
			} else {
				handleReprompt(item);
			}
		} catch {
			// 数据坏了就当没跳转过，用户还能手动填
		}
	}, [handleReprompt, handleRegenerate]);

	useEffect(() => {
		if (!pendingStartImage || mode !== "start") {
			return;
		}
		setPendingStartImage(null);
		setImageAt(0, { previewUrl: pendingStartImage });
	}, [pendingStartImage, mode, setImageAt]);

	const handleUseSample = useCallback(
		(example: I2VSampleExample) => {
			// 带参考视频的样片（/genjutsu-ai）走 setExample，视频连同图和 prompt 一次回填
			if (example.referenceMedia?.length) {
				setExample({
					mode: example.mode,
					model: example.modelId,
					prompt: example.prompt,
					images: example.inputImages,
					aspectRatio: example.aspectRatio ?? "auto",
					duration: example.duration,
					referenceMedia: example.referenceMedia,
				});
				setResolution(example.resolution);
				scrollToForm();
				return;
			}
			setMode(example.mode);
			setPendingSample(example);
			scrollToForm();
		},
		[setMode, setExample, setResolution, scrollToForm],
	);

	useEffect(() => {
		if (!pendingSample || mode !== pendingSample.mode) {
			return;
		}
		const example = pendingSample;
		setPendingSample(null);
		setModel(example.modelId);
		setResolution(example.resolution);
		setDuration(example.duration);
		if (example.aspectRatio) {
			setAspectRatio(example.aspectRatio);
		}
		if (example.mode === "reference") {
			// reference 是一个不带空洞的列表，整组一次追加
			addExistingImages(example.inputImages);
		} else {
			// start 只有首帧，between 是 [首帧, 尾帧]，按下标对号入座
			example.inputImages.forEach((url, index) => {
				setImageAt(index, { previewUrl: url });
			});
		}
		setPrompt(example.prompt);
	}, [
		pendingSample,
		mode,
		addExistingImages,
		setAspectRatio,
		setDuration,
		setImageAt,
		setModel,
		setPrompt,
		setResolution,
	]);

	// 落地页样片区（/reference-to-video）的「Try this example」发的是首页 hero 的事件，接过来用 hook
	// 的 setExample 一次回填（按目标模型的上限放图、清掉原来的参考）；按钮自己会滚回工作台
	useEffect(() => {
		const onExample = (event: Event) => {
			const example = (event as CustomEvent<HeroShowcaseExample>).detail;
			if (example && example.mode !== "text") {
				setExample(example);
			}
		};
		window.addEventListener(VIDEO_HERO_EXAMPLE_EVENT, onExample);
		return () =>
			window.removeEventListener(VIDEO_HERO_EXAMPLE_EVENT, onExample);
	}, [setExample]);

	// 下拉和首页 hero 同一份名单、同样显示真实模型名（2026-10-06 老公定）
	// 改视频页只给传进来的那几个（Mini + Wan 3.0）
	const listedModels = videoEdit?.models ?? HOME_HERO_MODELS;
	const availableModels = listedModels.filter((id) =>
		getI2VModel(id).modes.includes(mode),
	);
	const modelFamily = getI2VModelFamily(model);
	const menuModels = collapseI2VModelFamilies(availableModels);
	// 名单里有收参考视频 / 音频的档，参考格就收（当前档不收会自动切过去）
	const fallbackLimits = <K extends "referenceVideo" | "referenceAudio">(
		key: K,
	) => {
		const id = availableModels.find((item) =>
			key === "referenceVideo"
				? supportsI2VReferenceVideo(item, mode)
				: supportsI2VReferenceAudio(item, mode),
		);
		return id ? getI2VModel(id)[key] : undefined;
	};
	const referenceVideoMax = (videoLimits ?? fallbackLimits("referenceVideo"))
		?.maxCount;
	const referenceAudioMax = (audioLimits ?? fallbackLimits("referenceAudio"))
		?.maxCount;
	const referenceAccept = [
		"image/jpeg,image/png,image/webp",
		...(referenceVideoMax || referenceAudioMax
			? ACCEPTED_REFERENCE_MEDIA_TYPES
			: []),
	].join(",");
	// 加了视频 / 音频后不收那一类的档灰掉，返回灰掉的原因文案 key
	const blockedReason = (id: I2VModelId) =>
		referenceVideos.length && !supportsI2VReferenceVideo(id, mode)
			? "model.noVideoReference"
			: referenceAudios.length && !supportsI2VReferenceAudio(id, mode)
				? "model.noAudioReference"
				: null;
	useEffect(() => {
		if (videoEdit) {
			if (!videoEdit.models.includes(model)) {
				setModel(videoEdit.models[0]);
			}
			return;
		}
		if (!HOME_HERO_MODELS.includes(model)) {
			setModel(toListedI2VModel(model, mode, resolution));
		}
	}, [videoEdit, model, mode, resolution, setModel]);
	const promptPlaceholder = t(
		mode === "between"
			? "prompt.placeholderBetween"
			: mode === "reference"
				? referenceMedia.length
					? "prompt.placeholderReferenceVideo"
					: // 2 张以上才教「按编号指图」，跟 /ai-edit-image 一样
						filledImages.length >= 2
						? "prompt.placeholderReferenceRefs"
						: "prompt.placeholderReference"
				: "prompt.placeholderStart",
	);
	const durationOptions = getI2VDurationOptions(model, mode);
	const isPromptTooLong = prompt.length > getI2VPromptMaxLength(model, mode);
	const familyVariants =
		modelFamily?.variants.filter((variant) =>
			availableModels.includes(variant.id),
		) ?? [];
	// 配置条依次显示比例、分辨率、时长；固定画幅模式不显示比例，多出片才带张数
	const settingsParts = [
		...(canPickAspectRatio
			? [
					aspectRatio === "auto"
						? t("settings.autoAspectRatio")
						: aspectRatio,
				]
			: []),
		resolution.toUpperCase(),
		`${duration}s`,
		...(outputs > 1 ? [`${outputs}x`] : []),
	];
	const isIncomplete = !hasRequiredImages || !prompt.trim();
	// 缺图/缺字不再弹提示（10/21 老公定）：登录用户点了就让缺的那一栏闪红框；
	// 访客照旧走 handleGenerate，它先查登录、弹登录框
	const isBlocked = !!user && isIncomplete;
	const handleCreateClick = () => {
		if (isBlocked) {
			flashMissingField(
				hasRequiredImages
					? document.getElementById("i2v-prompt")
					: formRef.current?.querySelector(
							'[data-missing-field="image"]',
						),
			);
			return;
		}
		void handleGenerate();
	};

	return (
		// 外框和 /ai-edit-image 工作台一致（10/20 老公定）：分栏起铺满、左白右灰、中间可拖，
		// 左栏宽度和那边共用一个变量和存储；断点见 globals.css 的 studio-split。
		// 手机上同样不套卡片，内容离屏幕边只留 (saas) main 的 16（10/21）
		<div className="studio-theme flex flex-col gap-3 bg-background p-1 max-sm:p-0 sm:gap-4 sm:p-4 studio-split:h-[calc(100vh-var(--language-banner-height,0px))] studio-split:min-h-[560px] studio-split:flex-row studio-split:items-stretch studio-split:gap-0 studio-split:overflow-hidden studio-split:p-0">
			{/* 左：表单 */}
			<aside
				ref={formRef}
				className="group/form flex w-full min-h-0 flex-shrink-0 flex-col overflow-hidden max-md:overflow-clip rounded-xl border border-border/15 bg-card shadow-sm max-sm:rounded-none max-sm:border-0 max-sm:bg-transparent max-sm:shadow-none studio-split:w-[var(--left-panel-w,412px)] studio-split:max-w-[calc(100%-280px)] studio-split:self-stretch studio-split:rounded-none studio-split:border-0 studio-split:shadow-none"
			>
				<div className="flex-shrink-0 p-3 pb-0 max-sm:px-0 max-sm:pt-0 sm:p-6 studio-split:px-4 studio-split:pt-4">
					{/* 字距跟 /ai-edit-image 走 studio-theme，不另加 tracking-tight；英文 H1 长，24px 在默认 412 宽的左栏折两行显得又大又重，
					    22px 刚好一行（10/21 老公嫌重） */}
					<h1 className="mb-3 text-xl font-bold sm:mb-4 sm:text-[22px]">
						{titleOverride ?? t("heading")}
					</h1>
					{subtitleOverride ? (
						<p className="mb-3 text-[13px] leading-normal text-muted-foreground sm:mb-4">
							{subtitleOverride}
						</p>
					) : null}

					{/* 三种输入范式。手机和分栏起都按文字长短分宽（分栏起照 /ai-edit-image、13px）：
					    中间那个改叫首尾帧后西语最长（Fotograma inicial y final），固定比例在 375 宽会被截断；
					    默认 412 宽三个都单行，左栏拖窄了折两行不截断 */}
					{/* 改视频页锁在 Reference，不出 tab */}
					{videoEdit ? null : (
						<div
							role="tablist"
							aria-label={t("tabsLabel")}
							className="flex gap-1 rounded-xl bg-zinc-100 p-1 [&>button]:flex-auto sm:grid sm:grid-cols-3 studio-split:flex studio-split:gap-0.5 studio-split:[&>button]:px-1 studio-split:[&>button]:text-[13px]"
						>
							{I2V_MODES.map((item) => (
								<button
									key={item}
									type="button"
									role="tab"
									aria-selected={mode === item}
									onClick={() => setMode(item as I2VMode)}
									className={cn(
										"min-w-0 whitespace-nowrap rounded-lg px-1 py-2 text-center text-[11px] font-medium leading-snug transition-colors sm:text-sm studio-split:whitespace-normal",
										mode === item
											? "bg-white text-zinc-900 shadow-[0_1px_3px_rgba(0,0,0,0.08)]"
											: "text-zinc-500 hover:text-zinc-800",
									)}
								>
									<span className="block truncate studio-split:overflow-visible studio-split:whitespace-normal">
										{t(`tabs.${item}`)}
									</span>
								</button>
							))}
						</div>
					)}
				</div>

				{/* 滚动区本身就是 flex 列，内容不够高时空白留在设置条下面；设置条跟着上传、prompt 走，底部只钉 Create（10/24 老公定） */}
				<div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto overflow-x-hidden px-3 pt-2 pb-3 max-sm:px-0 sm:gap-4 sm:px-6 sm:pt-3 sm:pb-6 studio-split:px-4">
					{/* 模型卡：照 AIReel 放在 tab 下面、上传区上面。视频模型决定价格、分辨率、时长和能不能带参考，
					    是第一个要做的选择，不该藏在设置弹框里（10/24 老公定，生图页暂不跟）；档位按钮跟在卡片下面 */}
					<div className="flex shrink-0 flex-col gap-2">
						<ModelPicker
							open={modelMenuOpen}
							onOpenChange={setModelMenuOpen}
							title={tImage("labels.model")}
							listClassName="space-y-1"
							trigger={
								<button
									type="button"
									className="block w-full rounded-xl border border-zinc-200 bg-background px-3 py-1.5 text-left transition-colors hover:bg-zinc-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 data-[state=open]:bg-zinc-50"
								>
									{/* 照 AIReel：Model 单独一行靠左，logo 跟模型名同一行，整张卡约 52 高（10/24 老公定） */}
									<span className="block text-xs leading-4 text-zinc-500">
										{tImage("labels.model")}
									</span>
									<span className="mt-0.5 flex items-center gap-2">
										<ModelBrandIcon
											brand={getI2VModelBrand(model)}
											className="size-[18px]"
										/>
										<span className="min-w-0 flex-1 truncate text-sm font-medium leading-5 text-zinc-900">
											{modelFamily?.name ??
												getModelDisplayName(model)}
										</span>
										<ChevronDownIcon className="h-4 w-4 flex-shrink-0 text-zinc-500 lg:-rotate-90" />
									</span>
								</button>
							}
						>
							{menuModels.map((id) => {
								const config = getI2VModel(id);
								// 各档的分辨率 / 时长口径并不一致，价格旁边必须
								// 标出来，否则横向比就是误导（见 getI2VModelPriceHint）
								const hint = getI2VModelPriceHint(
									id as I2VModelId,
									referenceVideoSeconds,
								);
								const family = getI2VModelFamily(id);
								// 多档族这一条代表整族，已在族里时选它不换档
								const selected = family
									? family === modelFamily
									: id === model;
								const blocked = blockedReason(id as I2VModelId);
								return (
									<ModelPickerItem
										key={id}
										disabled={!!blocked}
										onSelect={() => {
											if (!selected) {
												changeModel(id as I2VModelId);
											}
										}}
										className={cn(
											"flex w-full cursor-pointer items-start justify-between gap-3 rounded-md px-2 py-2 text-left",
											selected && "bg-muted/60",
										)}
									>
										<span className="min-w-0">
											<span className="flex items-center gap-2 text-sm font-medium">
												<ModelBrandIcon
													brand={getI2VModelBrand(
														id as I2VModelId,
													)}
												/>
												{family?.name ??
													getModelDisplayName(
														id as I2VModelId,
													)}
											</span>
											<span className="block text-xs text-muted-foreground">
												{blocked
													? t(blocked)
													: t(
															`model.tier${config.tier}`,
														)}
											</span>
										</span>
										<span className="flex flex-shrink-0 flex-col items-end gap-0.5 text-muted-foreground">
											<span className="flex items-center gap-1 text-xs">
												<GemIcon className="h-3 w-3" />
												{hint.credits}
											</span>
											<span className="text-[10px] leading-none">
												{`${hint.resolution.toUpperCase()} · ${hint.duration}s`}
											</span>
										</span>
									</ModelPickerItem>
								);
							})}
						</ModelPicker>

						{/* 档位：照 imgveo，积分按当前分辨率和时长算 */}
						{/* 改视频页把 Wan 3.0 Prime 拿掉了，族里只剩一档就不出档位按钮 */}
						{familyVariants.length > (videoEdit ? 1 : 0) ? (
							<div
								className={cn(
									"grid gap-2",
									familyVariants.length === 2
										? "grid-cols-2"
										: "grid-cols-3",
								)}
							>
								{familyVariants.map((variant) => (
									<button
										key={variant.id}
										type="button"
										disabled={!!blockedReason(variant.id)}
										onClick={() => changeModel(variant.id)}
										className={cn(
											SETTINGS_OPTION_CLASS,
											"gap-1.5",
											variant.id === model
												? SETTINGS_OPTION_SELECTED
												: SETTINGS_OPTION_IDLE,
										)}
									>
										{variant.name}
										<span className="flex items-center gap-0.5 text-[11px] font-normal opacity-70">
											<GemIcon className="h-3 w-3" />
											{getI2VCreditCost(
												variant.id,
												resolution,
												duration,
												referenceVideoSeconds,
											)}
										</span>
									</button>
								))}
							</div>
						) : null}
					</div>
					<div className="flex shrink-0 flex-col">
						{mode === "start" ? (
							<FrameSlot
								image={inputImages[0] ?? null}
								title={t("slot.startTitle")}
								selectAssetLabel={t("slot.selectAsset")}
								className={SLOT_MIN_HEIGHT}
								onFiles={(files) => handleSlotFiles(0, files)}
								onPickAsset={() => openAssetPicker(0)}
								onPreview={() => setPreviewIndex(0)}
								onEdit={() => setEditIndex(0)}
								onRemove={() => removeImage(0)}
							/>
						) : null}

						{mode === "between" ? (
							<div className="flex items-center gap-2">
								<div className="min-w-0 flex-1">
									<FrameSlot
										image={inputImages[0] ?? null}
										title={t("slot.clickDrop")}
										subtitle={t("slot.startHint")}
										selectAssetLabel={t("slot.selectAsset")}
										className={SLOT_MIN_HEIGHT_SPLIT}
										compact
										onFiles={(files) =>
											handleSlotFiles(0, files)
										}
										onPickAsset={() => openAssetPicker(0)}
										onPreview={() => setPreviewIndex(0)}
										onEdit={() => setEditIndex(0)}
										onRemove={() => removeImage(0)}
									/>
								</div>
								<ChevronsRightIcon className="h-5 w-5 flex-shrink-0 text-zinc-400" />
								<div className="min-w-0 flex-1">
									<FrameSlot
										image={inputImages[1] ?? null}
										title={t("slot.clickDrop")}
										subtitle={t("slot.endHint")}
										selectAssetLabel={t("slot.selectAsset")}
										className={SLOT_MIN_HEIGHT_SPLIT}
										compact
										onFiles={(files) =>
											handleSlotFiles(1, files)
										}
										onPickAsset={() => openAssetPicker(1)}
										onPreview={() => setPreviewIndex(1)}
										onEdit={() => setEditIndex(1)}
										onRemove={() => removeImage(1)}
									/>
								</div>
							</div>
						) : null}

						{mode === "reference" && musicVideo ? (
							<MusicVideoUploads
								audio={referenceAudios[0] ?? null}
								images={filledImages}
								minSeconds={audioLimits?.minSeconds ?? 4}
								maxSeconds={audioLimits?.maxTotalSeconds ?? 60}
								defaultSeconds={modelConfig.duration.default}
								labels={musicVideo.labels}
								selectAssetLabel={t("slot.selectAsset")}
								processingLabel={(item) =>
									t("slot.mediaProcessing", {
										percent: Math.round(
											item.progress * 100,
										),
									})
								}
								onClip={setDrivingAudio}
								onRemoveAudio={removeReferenceMedia}
								onPhotoFiles={handleReferenceFiles}
								onPickAsset={() => openAssetPicker("append")}
								onPreviewImage={setPreviewIndex}
								onEditImage={setEditIndex}
								onRemoveImage={removeReferenceImage}
							/>
						) : null}

						{mode === "reference" &&
						videoEdit &&
						videoEdit.uploads !== "audio" ? (
							<VideoEditUploads
								video={referenceVideos[0] ?? null}
								images={filledImages}
								maxImages={maxImages}
								labels={videoEdit.labels}
								selectAssetLabel={t("slot.selectAsset")}
								removeVideoLabel={t("slot.removeVideo")}
								processingLabel={(item) =>
									t("slot.mediaProcessing", {
										percent: Math.round(
											item.progress * 100,
										),
									})
								}
								onFiles={handleReferenceFiles}
								onRemoveVideo={removeReferenceMedia}
								onPickAsset={() => openAssetPicker("append")}
								onPreviewImage={setPreviewIndex}
								onEditImage={setEditIndex}
								onRemoveImage={removeReferenceImage}
							/>
						) : null}

						{mode === "reference" && !videoEdit ? (
							<div className="flex flex-col gap-2">
								{/* 不放标题：上传区自己写了能传什么，上限在传图后的计数行（10/22 老公定） */}
								<input
									ref={referenceInputRef}
									type="file"
									multiple
									accept={referenceAccept}
									className="hidden"
									onChange={(event) => {
										handleReferenceFiles(
											Array.from(
												event.target.files ?? [],
											),
										);
										event.target.value = "";
									}}
								/>
								{filledImages.length === 0 &&
								referenceMedia.length === 0 ? (
									<div
										data-missing-field="image"
										className={cn(
											// 照 /ai-edit-image：灰底 + 1px 细虚线 + 圆角 16
											"flex w-full flex-col items-center justify-center rounded-2xl border border-dashed p-3 text-center transition-colors sm:p-6",
											SLOT_MIN_HEIGHT,
											isDragOver
												? "cursor-pointer border-blue-400 bg-blue-50"
												: "cursor-pointer border-zinc-300 bg-zinc-100 hover:border-zinc-400 hover:bg-zinc-200/60",
										)}
										onClick={() =>
											referenceInputRef.current?.click()
										}
										onDragOver={(event) => {
											event.preventDefault();
											setIsDragOver(true);
										}}
										onDragLeave={() => setIsDragOver(false)}
										onDrop={(event) => {
											event.preventDefault();
											setIsDragOver(false);
											handleReferenceFiles(
												Array.from(
													event.dataTransfer.files,
												),
											);
										}}
									>
										<button
											type="button"
											onClick={(event) => {
												event.stopPropagation();
												referenceInputRef.current?.click();
											}}
											className="flex flex-col items-center"
										>
											<UploadIcon className="mb-3 size-7 text-zinc-700" />
											<span className="text-sm text-zinc-500">
												{referenceVideoMax ||
												referenceAudioMax
													? t("slot.referenceHintAll")
													: t("slot.referenceHint")}
											</span>
											<span className="mt-0.5 text-xs text-zinc-400">
												{t("slot.referenceSubtitle")}
											</span>
										</button>
										<Button
											type="button"
											variant="outline"
											size="sm"
											onClick={(event) => {
												event.stopPropagation();
												openAssetPicker("append");
											}}
											// 次要入口，退到上传文案后面：透明底细框灰字；min-h-0 抵掉 Button sm 的 44（10/21 老公选 C）
											className="mt-4 h-7.5 min-h-0 gap-1.5 rounded-full border-zinc-300 bg-transparent px-3.5 text-xs font-medium text-zinc-500 hover:bg-white hover:text-zinc-700"
										>
											<FolderIcon className="h-3.5 w-3.5" />
											{t("slot.selectAsset")}
										</Button>
									</div>
								) : (
									<div className="space-y-3">
										<div className="grid grid-cols-3 gap-3">
											{filledImages.map(
												(image, index) => (
													<div
														key={image.previewUrl}
														className={cn(
															"group relative aspect-square overflow-hidden rounded-lg border border-border bg-muted/20",
															// 光标停在 prompt 里的「Image N」上时描边这张
															activeImageRef ===
																index + 1 &&
																"ring-2 ring-primary ring-offset-2",
														)}
													>
														<img
															src={buildDisplayImageUrl(
																image.previewUrl,
																384,
															)}
															alt={formatImageRef(
																index + 1,
															)}
															className="h-full w-full object-cover"
														/>
														{/* 2 张以上才标编号，单张看到的和原来一样（照 /ai-edit-image） */}
														{filledImages.length >=
															2 && (
															<span className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/65 to-transparent px-2 pt-4 pb-1.5 text-xs font-semibold text-white">
																{formatImageRef(
																	index + 1,
																)}
															</span>
														)}
														<UploadThumbnailActions
															onPreview={() =>
																setPreviewIndex(
																	index,
																)
															}
															onEdit={() =>
																setEditIndex(
																	index,
																)
															}
															onDelete={() =>
																removeReferenceImage(
																	index,
																)
															}
														/>
													</div>
												),
											)}
											{referenceMedia.map((item) => (
												<ReferenceMediaThumb
													key={item.id}
													media={item}
													processingLabel={t(
														"slot.mediaProcessing",
														{
															percent: Math.round(
																item.progress *
																	100,
															),
														},
													)}
													removeLabel={t(
														item.kind === "video"
															? "slot.removeVideo"
															: "slot.removeAudio",
													)}
													onRemove={() =>
														removeReferenceMedia(
															item.id,
														)
													}
													className="aspect-square rounded-lg border border-border"
												/>
											))}
											{filledImages.length < maxImages ||
											(referenceVideoMax &&
												referenceVideos.length <
													referenceVideoMax) ||
											(referenceAudioMax &&
												referenceAudios.length <
													referenceAudioMax) ? (
												// 「+」只管从设备传图（点或拖），素材库入口在下面那一行，跟生图页一样
												<button
													type="button"
													onClick={() =>
														referenceInputRef.current?.click()
													}
													onDragOver={(event) => {
														event.preventDefault();
														setIsDragOver(true);
													}}
													onDragLeave={() =>
														setIsDragOver(false)
													}
													onDrop={(event) => {
														event.preventDefault();
														setIsDragOver(false);
														handleReferenceFiles(
															Array.from(
																event
																	.dataTransfer
																	.files,
															),
														);
													}}
													className={cn(
														"flex aspect-square w-full items-center justify-center rounded-lg border-2 border-dashed text-3xl font-light text-zinc-500",
														isDragOver
															? "border-blue-400 bg-blue-50"
															: "border-zinc-300 bg-zinc-100 hover:bg-zinc-200",
													)}
												>
													+
												</button>
											) : null}
										</div>
										{/* 跟生图页一模一样：左边计数，右边 Select asset + Clear all */}
										<div className="flex items-center justify-between gap-2">
											{/* 视频 / 音频传了才显示，0 / 3 不占位；每段不拆行，要折只在 · 处折 */}
											<p className="text-xs tabular-nums text-zinc-500">
												<span className="whitespace-nowrap">
													{tImage(
														"imageSettings.upload.counter",
														{
															current:
																filledImages.length,
															max: maxImages,
														},
													)}
												</span>
												{referenceVideoMax &&
												referenceVideos.length ? (
													<span className="whitespace-nowrap">
														{` · ${t(
															"slot.videoCounter",
															{
																count: referenceVideos.length,
																max: referenceVideoMax,
															},
														)}`}
													</span>
												) : null}
												{referenceAudioMax &&
												referenceAudios.length ? (
													<span className="whitespace-nowrap">
														{` · ${t(
															"slot.audioCounter",
															{
																count: referenceAudios.length,
																max: referenceAudioMax,
															},
														)}`}
													</span>
												) : null}
											</p>
											<div className="flex shrink-0 gap-2">
												{filledImages.length <
													maxImages && (
													<Button
														type="button"
														variant="outline"
														size="sm"
														onClick={() =>
															openAssetPicker(
																"append",
															)
														}
														// 已传图后的这一排和上传区那颗 My Assets 同一套小胶囊，Clear all 跟着同高（10/21）
														className="h-7.5 min-h-0 gap-1.5 rounded-full border-zinc-300 bg-transparent px-3 text-xs font-medium text-zinc-500 hover:bg-white hover:text-zinc-700"
													>
														<FolderIcon className="h-3.5 w-3.5" />
														{t("slot.selectAsset")}
													</Button>
												)}
												<Button
													type="button"
													variant="outline"
													size="sm"
													onClick={() => {
														// prompt 里的编号一起删；视频、音频恢复不了，这里不给 Undo
														const nextPrompt =
															dropAllImageRefs(
																prompt,
															);
														if (
															nextPrompt !==
															prompt
														) {
															setPrompt(
																nextPrompt,
															);
															// 还开着的删图 Undo 对不上了：原地换成这条（理由同删图）
															showRefToast(
																tImage(
																	"imageRefs.clearedFromPrompt",
																),
															);
															removeUndoRef.current =
																null;
														}
														// 参考视频和音频跟图片在同一个网格里，一起清
														clearImages();
														for (const item of referenceMedia) {
															removeReferenceMedia(
																item.id,
															);
														}
													}}
													className="h-7.5 min-h-0 rounded-full border-red-200 bg-transparent px-3 text-xs font-medium text-red-500 hover:bg-red-50 hover:text-red-600"
												>
													{tImage(
														"imageSettings.upload.clearAll",
													)}
												</Button>
											</div>
										</div>
									</div>
								)}
							</div>
						) : null}
					</div>

					{/* Prompt。照 /ai-edit-image 固定高度，不再吃满剩余高度（10/22 老公嫌太高） */}
					<div
						ref={promptSectionRef}
						className="flex shrink-0 flex-col"
					>
						<Label
							htmlFor="i2v-prompt"
							className="flex-shrink-0 text-sm font-medium text-zinc-500"
						>
							{t("prompt.label")}
						</Label>
						{promptPresets?.length && mode === "reference" ? (
							<div className="mt-2 flex flex-wrap gap-1.5">
								{promptPresets.map((preset) => {
									const active = prompt === preset.prompt;
									return (
										<button
											key={preset.id}
											type="button"
											aria-pressed={active}
											onClick={() =>
												setPrompt(preset.prompt)
											}
											className={cn(
												"h-8 rounded-full px-3 font-medium text-[13px] transition-colors",
												active
													? SETTINGS_OPTION_SELECTED
													: SETTINGS_OPTION_IDLE,
											)}
										>
											{preset.label}
										</button>
									);
								})}
							</div>
						) : null}
						<div
							ref={promptBoxRef}
							className="relative mt-2 flex min-h-0 flex-1"
						>
							<PromptTextarea
								id="i2v-prompt"
								handleRef={promptHandleRef}
								highlightImageCount={
									mode === "reference"
										? filledImages.length
										: 0
								}
								onActiveImageRefChange={setActiveImageRef}
								onAtTrigger={
									canReferenceImages
										? (active) =>
												active
													? openRefPicker("typed")
													: closeRefPicker()
										: undefined
								}
								onKeyDown={(event) => {
									if (
										refPickerHandleRef.current?.handleKeyDown(
											event,
										)
									) {
										event.preventDefault();
									}
								}}
								value={prompt}
								onChange={setPrompt}
								maxLength={getI2VPromptMaxLength(model, mode)}
								placeholder={promptPlaceholder}
								className={cn(
									"min-h-[120px] flex-1 resize-none rounded-2xl border-zinc-200 bg-card p-3 pb-8 text-[15px] leading-relaxed placeholder:text-zinc-400 focus:border-primary focus:ring-primary sm:p-4 sm:text-sm md:[@media(min-height:950px)]:min-h-[200px]",
									// 底部留出 @ Reference 按钮那一行，字不压到按钮上
									canReferenceImages &&
										"min-h-[140px] pb-12 sm:pb-12",
								)}
							/>
							{canReferenceImages && (
								<>
									<button
										ref={refButtonRef}
										type="button"
										aria-expanded={isRefPickerOpen}
										onClick={() =>
											isRefPickerOpen
												? closeRefPicker()
												: openRefPicker("button")
										}
										// 不抢输入框的焦点：光标留在原处，方向键 / 回车还能直接选图
										onMouseDown={(event) =>
											event.preventDefault()
										}
										className="absolute bottom-2 left-3 inline-flex h-7.5 items-center gap-1 rounded-full bg-zinc-100 pr-3 pl-2.5 text-xs font-medium text-zinc-700 transition-colors hover:bg-zinc-200 aria-expanded:bg-primary aria-expanded:text-white sm:left-4"
									>
										<AtSignIcon className="size-3.5" />
										{tImage("imageRefs.button")}
									</button>
									<ImageRefPicker
										handleRef={refPickerHandleRef}
										open={isRefPickerOpen}
										onClose={closeRefPicker}
										inputImages={filledImages.map(
											(image) => image.previewUrl,
										)}
										maxImages={maxImages}
										isLoggedIn={!!user}
										onPickExisting={insertExistingImageRef}
										onPickAsset={insertAssetImageRef}
										onBrowseAll={browseAllForRef}
										ignoreRef={refButtonRef}
										anchorRef={promptBoxRef}
									/>
								</>
							)}
						</div>
						{missingImageRef !== null && (
							<p className="mt-1.5 text-xs text-amber-700">
								{tImage("imageRefs.missing", {
									n: missingImageRef,
									count: filledImages.length,
								})}
							</p>
						)}
					</div>
					{/* 配置条分区显示比例、分辨率和时长，模型在上面的模型卡里。
					    modal={false}：这是个多控件面板，本来就靠点外面关；默认 modal 会把关闭那一下点击吞掉，调完时长再点 Create 没反应 */}
					{/* 对口型 MV 页：时长跟歌、比例跟照片、只有 720p、一次一条，没东西可调 */}
					{musicVideo ? null : (
						<GenerationSettings
							modal={false}
							trigger={
								<button
									type="button"
									className={SETTINGS_BAR_CLASS}
								>
									{settingsParts.map((part) => (
										<span
											key={part}
											className="flex h-5 flex-1 items-center justify-center whitespace-nowrap border-l border-zinc-300/70 px-2 tabular-nums first:border-l-0"
										>
											{part}
										</span>
									))}
									<ChevronDownIcon className="ml-1 h-4 w-4 flex-shrink-0 text-zinc-500 transition-transform group-data-[state=open]:rotate-180 lg:-rotate-90 lg:group-data-[state=open]:-rotate-90" />
								</button>
							}
						>
							<div className="space-y-4">
								{/* 按模型与输入模式显示比例，固定画幅模式显示 Auto */}
								{aspectRatioOptions.length ? (
									<div className="space-y-2">
										<p className={SETTINGS_LABEL_CLASS}>
											{t("settings.aspectRatio")}
										</p>
										<div className="grid grid-cols-4 gap-2">
											{aspectRatioOptions.map((ratio) => {
												const isSelected =
													canPickAspectRatio &&
													ratio === aspectRatio;
												return (
													<button
														key={ratio}
														disabled={
															!canPickAspectRatio
														}
														type="button"
														onClick={() =>
															setAspectRatio(
																ratio as I2VAspectRatio,
															)
														}
														className={cn(
															SETTINGS_OPTION_CLASS,
															"gap-1.5 text-xs",
															isSelected
																? SETTINGS_OPTION_SELECTED
																: SETTINGS_OPTION_IDLE,
														)}
													>
														<AspectRatioSwatch
															value={ratio}
															size={14}
															className={cn(
																"border bg-transparent",
																isSelected
																	? "border-white"
																	: "border-zinc-500/70",
															)}
														/>
														<span>
															{ratio === "auto"
																? t(
																		"settings.autoAspectRatio",
																	)
																: ratio}
														</span>
													</button>
												);
											})}
										</div>
										{!canPickAspectRatio && (
											<p className="text-xs text-muted-foreground">
												{tVideo("aspectRatioHint", {
													min: 2,
												})}
											</p>
										)}
									</div>
								) : null}

								{/* 分辨率 */}
								<div className="space-y-2">
									<p className={SETTINGS_LABEL_CLASS}>
										{t("settings.resolution")}
									</p>
									<div
										className={cn(
											"grid gap-2",
											modelConfig.resolutions.length === 4
												? "grid-cols-4"
												: modelConfig.resolutions
															.length === 2
													? "grid-cols-2"
													: "grid-cols-3",
										)}
									>
										{modelConfig.resolutions.map((item) => {
											const locked =
												lockedResolutions.has(item);
											return (
												<button
													key={item}
													disabled={
														model === "fast-3.0" &&
														item === "1080p" &&
														inputImages.filter(
															Boolean,
														).length > 1
													}
													type="button"
													onClick={() => {
														if (locked) {
															setPaywallReason(
																"video-resolution",
															);
															return;
														}
														setResolution(
															item as I2VResolution,
														);
													}}
													className={cn(
														SETTINGS_OPTION_CLASS,
														// 当前档可能本身就锁着（游客选 Veo 默认 720P），选中态优先，锁图标照样显示
														item === resolution
															? SETTINGS_OPTION_SELECTED
															: locked
																? "bg-zinc-100 text-zinc-500 opacity-40 hover:opacity-60"
																: SETTINGS_OPTION_IDLE,
													)}
												>
													{locked ? (
														<LockIcon className="h-3 w-3" />
													) : null}
													{item.toUpperCase()}
												</button>
											);
										})}
									</div>
								</div>

								{/* 时长 */}
								<div className="space-y-2">
									<div className="flex items-center justify-between">
										<p className={SETTINGS_LABEL_CLASS}>
											{t("settings.duration")}
										</p>
										<span className="text-xs font-medium tabular-nums text-zinc-900">
											{duration}s
										</span>
									</div>
									{durationOptions ? (
										<div className="grid grid-cols-3 gap-2">
											{durationOptions.map((item) => (
												<button
													key={item}
													type="button"
													onClick={() =>
														setDuration(item)
													}
													className={cn(
														SETTINGS_OPTION_CLASS,
														item === duration
															? SETTINGS_OPTION_SELECTED
															: SETTINGS_OPTION_IDLE,
													)}
												>
													{item}s
												</button>
											))}
										</div>
									) : (
										<>
											<Slider
												className="px-1 pt-1"
												min={modelConfig.duration.min}
												max={modelConfig.duration.max}
												step={1}
												value={[duration]}
												onValueChange={([value]) =>
													setDuration(value)
												}
											/>
											<div className="mt-1.5 flex justify-between text-[10px] text-muted-foreground">
												<span>
													{modelConfig.duration.min}s
												</span>
												<span>
													{modelConfig.duration.max}s
												</span>
											</div>
										</>
									)}
								</div>

								{/* 出片数量 */}
								<div className="space-y-2">
									<p className={SETTINGS_LABEL_CLASS}>
										{t("settings.outputs")}
									</p>
									<div className="grid grid-cols-4 gap-2">
										{modelConfig.outputOptions.map(
											(item) => (
												<button
													key={item}
													type="button"
													onClick={() =>
														setOutputs(item)
													}
													className={cn(
														SETTINGS_OPTION_CLASS,
														item === outputs
															? SETTINGS_OPTION_SELECTED
															: SETTINGS_OPTION_IDLE,
													)}
												>
													{item}
												</button>
											),
										)}
									</div>
								</div>
							</div>
						</GenerationSettings>
					)}
				</div>

				{/* 底部控制区。手机上 Create 钉在屏幕底部（照 AIReel，10/25 老公定）：只在表单范围里钉，滑过表单就松开；
				    打字时不钉，免得叠在键盘上。aside 手机上用 overflow-clip 不用 hidden，hidden 会把 sticky 困在 aside 里 */}
				<div className="flex-shrink-0 bg-card p-4 pt-2 max-sm:bg-background max-sm:px-0 sm:p-6 max-md:sticky max-md:bottom-0 max-md:z-10 max-md:group-has-[textarea:focus]/form:static studio-split:px-4 studio-split:pb-4">
					{/* 照 /ai-edit-image：缺图/缺字时半透明，登录用户悬停是禁止光标，访客照旧能点（弹登录框）。
					    超字数仍然禁用 */}
					<Button
						type="button"
						onClick={handleCreateClick}
						disabled={isPending || isPromptTooLong}
						className={cn(
							"h-11 w-full rounded-full border-blue-500 bg-blue-500 text-sm font-semibold text-white transition-[background-color,opacity] hover:bg-blue-600 disabled:opacity-70",
							isIncomplete && "opacity-60",
							isBlocked &&
								"enabled:cursor-not-allowed hover:bg-blue-500",
						)}
					>
						{isPending ? (
							<>
								<Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
								{isUploading ? t("uploading") : t("generating")}
							</>
						) : (
							<>
								{t("create")}
								<GemIcon className="mx-1 ml-2 h-3.5 w-3.5 opacity-80" />
								{creditCost}
							</>
						)}
					</Button>
				</div>
			</aside>

			<PanelResizer
				width={leftPanelWidth}
				onCommit={commitLeftPanelWidth}
				label={tImage("resizePanels")}
			/>

			{/* 右：示例 / 模板 / 历史。分栏起灰底，历史条目本身是白卡片 */}
			<main className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-xl border border-zinc-200/20 bg-white shadow-sm max-sm:overflow-visible max-sm:rounded-none max-sm:border-0 max-sm:bg-transparent max-sm:shadow-none studio-split:rounded-none studio-split:border-0 studio-split:bg-zinc-100 studio-split:shadow-none">
				{/* 放在下面的分支外面：某个 tab 空了也还留得住切回去的入口。
				    访客没有作品，不出 History（入口要么能用要么不出现） */}
				<div className="flex-shrink-0 p-4 max-sm:px-0 sm:p-6">
					{/* 宽度和卡片间距照 /ai-edit-image 的历史区：最宽 5xl 居中、sm 起间距 24 */}
					<div className="mx-auto max-w-5xl">
						<HistoryFeedTabs
							view={activeView}
							onViewChange={setFeedView}
							showHistory={!!user}
							filter={historyFilter}
							onFilterChange={(filter) => {
								setHistoryFilter(filter);
								// 定死在作品：自动选的话，筛到空的那一类会被当成没作品跳回示例
								setFeedView("mine");
							}}
						/>
					</div>
				</div>
				{activeView === "examples" ? (
					<I2VSampleShowcase
						onUseExample={handleUseSample}
						examples={sampleExamples}
					/>
				) : activeView === "templates" ? (
					<WorkbenchTemplates
						kind="video"
						className="px-4 max-sm:px-0 sm:px-6"
					/>
				) : history.length > 0 ? (
					<div
						ref={historyScrollRef}
						className="flex-1 overflow-y-auto px-4 pb-6 max-sm:px-0 sm:px-6"
					>
						<div className="mx-auto max-w-5xl space-y-4 sm:space-y-6">
							{groupHistoryEntries(history).map((entry) =>
								entry.kind === "videos" ? (
									<div
										key={entry.items[0].id}
										ref={
											entry.items[0].batchId ===
											latestStartedBatchId
												? latestBatchRef
												: undefined
										}
										data-generation-batch-id={
											entry.items[0].batchId
										}
									>
										<VideoHistoryCard
											items={entry.items}
											// 整条历史都搬回来，不只是 prompt。只填 prompt 的话，
											// 刷新后的空表单没有输入图，Create 会一直是灰的
											onReprompt={handleReprompt}
											onRegenerate={handleRegenerate}
											isRegenerateDisabled={isPending}
											onOpenDetail={setSelectedVideo}
											actionLabels={actionLabels}
											onToggleFavorite={
												toggleVideoFavorite
											}
											isFavorite={isVideoFavorite}
											onDelete={handleDeleteVideo}
										/>
									</div>
								) : (
									<ImageHistoryCard
										key={entry.item.id}
										item={entry.item}
										onOpenDetail={(imageIndex) =>
											setSelectedImage({
												item: entry.item,
												imageIndex,
											})
										}
										onReprompt={() =>
											handleImageAction(
												entry.item,
												"reprompt",
											)
										}
										onRegenerate={() =>
											handleImageAction(
												entry.item,
												"regenerate",
											)
										}
										openDetailLabel={tAssets(
											"detail.title",
										)}
										actionLabels={actionLabels}
										onGenerateVideo={
											handleGenerateVideoFromImage
										}
										onDownload={(imageUrl) => {
											// 没有去水印权限的账号下载带水印版本；purchases 没拉到时不加，
											// 口径同 WatermarkedImage
											if (
												!purchasesLoading &&
												!purchasesError &&
												!canRemoveWatermark
											) {
												downloadWithWatermark(
													imageUrl,
												).catch(() =>
													toast.error(
														tAssets(
															"errors.downloadFailed",
														),
													),
												);
												return;
											}
											window.location.href = `/api/proxy-download?url=${encodeURIComponent(imageUrl)}`;
										}}
										onEdit={handleImageEdit}
										onDelete={(imageIndex) =>
											handleImageDelete(
												entry.item.id,
												imageIndex,
											)
										}
										onToggleFavorite={(imageIndex) =>
											handleImageFavoriteToggle(
												entry.item.id,
												imageIndex,
												isImageFavorite(
													entry.item,
													imageIndex,
												),
											)
										}
										isImageFavorite={(imageIndex) =>
											isImageFavorite(
												entry.item,
												imageIndex,
											)
										}
									/>
								),
							)}
							{hasNextPage ? (
								<div
									ref={loadMoreRef}
									className="flex justify-center py-4"
								>
									{isFetchingNextPage ? (
										<Loader2Icon className="h-5 w-5 animate-spin text-muted-foreground" />
									) : null}
								</div>
							) : null}
						</div>
					</div>
				) : isHistoryLoading ? (
					<div className="flex flex-1 items-center justify-center">
						<Loader2Icon className="h-5 w-5 animate-spin text-muted-foreground" />
					</div>
				) : (
					/* 某个 tab 空不代表用户没记录，退回示例区会让人以为记录丢了 */
					<div className="flex flex-1 items-center justify-center px-4 pb-6">
						<p className="text-muted-foreground">
							{tAssets("emptyFiltered")}
						</p>
					</div>
				)}
			</main>

			<CreditsPaywallModal
				open={paywallReason !== null}
				onOpenChange={(open) => !open && setPaywallReason(null)}
				reason={paywallReason ?? "insufficient-credits"}
			/>
			<SelectAssetDialog
				open={assetTarget !== null}
				onOpenChange={(open) => {
					if (!open) {
						setAssetTarget(null);
						// 选完会先走 onConfirm 再关；没选就关掉的话别把「插编号」留给下一次
						browseInsertsRefsRef.current = false;
						promptHandleRef.current?.cancelImageRef();
					}
				}}
				remainingSlots={
					assetTarget === "append"
						? maxImages - filledImages.length
						: 1
				}
				existingUrls={
					browseInsertsRefsRef.current
						? filledImages.map((image) => image.previewUrl)
						: undefined
				}
				onConfirm={handleAssetConfirm}
			/>
			<UploadPreviewDialog
				imageUrl={
					previewIndex !== null
						? ((mode === "reference"
								? filledImages[previewIndex]
								: inputImages[previewIndex]
							)?.previewUrl ?? null)
						: null
				}
				onClose={() => setPreviewIndex(null)}
			/>
			<EditUploadedImageDialog
				open={editIndex !== null}
				imageUrl={
					editIndex !== null
						? ((mode === "reference"
								? filledImages[editIndex]
								: inputImages[editIndex]
							)?.previewUrl ?? null)
						: null
				}
				onOpenChange={(open) => !open && setEditIndex(null)}
				onConfirm={handleEditConfirm}
			/>
			{selectedVideo ? (
				<VideoDetailModal
					isOpen={!!selectedVideo}
					onClose={() => setSelectedVideo(null)}
					video={selectedVideo}
					onReprompt={() => {
						handleReprompt(selectedVideo);
					}}
					onRegenerate={() => {
						handleRegenerate(selectedVideo);
					}}
					isFavorite={isVideoFavorite(selectedVideo)}
					onToggleFavorite={() => toggleVideoFavorite(selectedVideo)}
					onDelete={() => handleDeleteVideo(selectedVideo)}
				/>
			) : null}
			{selectedImage ? (
				<ImageDetailModal
					isOpen={!!selectedImage}
					onClose={() => setSelectedImage(null)}
					generation={selectedImage.item}
					initialImageIndex={selectedImage.imageIndex}
					onReprompt={() =>
						handleImageAction(selectedImage.item, "reprompt")
					}
					onRegenerate={() =>
						handleImageAction(selectedImage.item, "regenerate")
					}
					onGenerateVideo={handleGenerateVideoFromImage}
					isImageFavorite={(imageIndex) =>
						isImageFavorite(selectedImage.item, imageIndex)
					}
					onToggleFavorite={(imageIndex) =>
						handleImageFavoriteToggle(
							selectedImage.item.id,
							imageIndex,
							isImageFavorite(selectedImage.item, imageIndex),
						)
					}
					onDelete={(imageIndex) =>
						handleImageDelete(selectedImage.item.id, imageIndex)
					}
				/>
			) : null}
		</div>
	);
}
